
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import DeviceStatusBadge from 'features/Integrations/components/DeviceStatusBadge';
import {
  openStageSettings,
  openDeviceSettings,
  openUSGSSettings,
} from 'features/Integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparisionUtils';
import { useMemo, useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetLicenseInfoQuery } from 'services/integrations/integrations.api';
import { deviceImageList, getDynamicDeviceIcon } from 'utils/devices/icon.utils';
import { getDeviceLicenseLabel, getSystemLicenseLabel } from '../../../utils/license.utils';
import { hasDevicePermissionFromDevice } from '../utils/devicePermissionUtils';
import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';

const formatDeviceTypeLabel = (deviceType = '') => deviceType.replaceAll('_', ' ');

const getCategoryIcon = (categoryName) =>
  deviceImageList[categoryName] || deviceImageList['RESCALERT'];

const getSystemIcon = (systemType) => deviceImageList[systemType] || deviceImageList['RESCALERT'];

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const getSystemDisplayId = (system = {}) => system.id ?? system.system_id;

const getDeviceDisplayId = (device = {}) => device.node_id ?? device.device_id ?? device.id;

const dropdownHeaderClass =
  'd-none d-lg-flex row px-4 py-2 mx-0 small fw-semibold text-white opacity-75 border-bottom border-secondary bg-primary-shadow';
const nestedDropdownHeaderClass =
  'd-none d-lg-flex row px-2 py-2 mx-0 small fw-semibold text-white opacity-75 border-bottom border-secondary bg-primary-shadow';

const Table = ({
  deviceIndexMap,
  systemIndexMap,
  connectedSystemsHierarchy,
  openEditFormForSystemOrDevice,
  prepareSystemOrDeviceForDeletion,
  deviceRowIdEllipsisOpen,
  setDeviceRowIdEllipsisOpen,
  systemRowIdEllipsisOpen,
  setSystemRowIdEllipsisOpen,
}) => {
  const dispatch = useDispatch();
  const perms = usePerms();
  const { data: licenseInfo = {} } = useGetLicenseInfoQuery();
  const { isFetching: connectedDevicesIsFetching } = useGetConnectedDevicesQuery();
  const [openRows, setOpenRows] = useState({});

  const toggleRow = (rowKey) => {
    setOpenRows((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  };

  const openDeviceView = (device) => {
    if (!device?.id) return;

    if (DeviceIsSystemType(device, 'Flood')) {
      dispatch(openStageSettings(device.id));
      return;
    }

    if (device.system_type === 'USGS') {
      dispatch(openUSGSSettings(device.id));
      return;
    }

    dispatch(openDeviceSettings({ id: device.id, deviceSettings: device }));
  };

  const renderSystemActionMenu = (systemNode, menuKey) => {
    const system = systemNode.sourceSystem;
    const canEditOrDelete =
      perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditIntegrations']) ||
      perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteIntegrations']);

    if (!canEditOrDelete) return null;

    return (
      <BaseEllipsisMenu
        open={systemRowIdEllipsisOpen[menuKey]}
        onOpen={() =>
          setSystemRowIdEllipsisOpen((current) => ({
            ...current,
            [menuKey]: true,
          }))
        }
        onClose={() =>
          setSystemRowIdEllipsisOpen((current) => ({
            ...current,
            [menuKey]: false,
          }))
        }
      >
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditIntegrations']) && (
          <Button
            className="w-100 text-center"
            variant="primary"
            onClick={() => {
              openEditFormForSystemOrDevice('system', system);
              setSystemRowIdEllipsisOpen((current) => ({
                ...current,
                [menuKey]: false,
              }));
            }}
          >
            Edit
          </Button>
        )}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteIntegrations']) && (
          <Button
            className="w-100 text-center"
            variant="danger"
            onClick={() => {
              prepareSystemOrDeviceForDeletion(system);
              setSystemRowIdEllipsisOpen((current) => ({
                ...current,
                [menuKey]: false,
              }));
            }}
          >
            Remove
          </Button>
        )}
      </BaseEllipsisMenu>
    );
  };

  const renderDeviceActionMenu = (device, menuKey) => {
    const hasDeletePermission = hasDevicePermissionFromDevice(perms, device, 'delete');
    const hasCreateEditPermission = hasDevicePermissionFromDevice(perms, device, 'createEdit');

    if (!hasCreateEditPermission && !hasDeletePermission) {
      return <div className="col-12 col-md-1 col-lg-1" />;
    }

    return (
      <div
        className="col-12 col-md-1 col-lg-1 d-flex justify-content-md-end"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <BaseEllipsisMenu
          open={deviceRowIdEllipsisOpen[menuKey]}
          onOpen={() =>
            setDeviceRowIdEllipsisOpen((current) => ({
              ...current,
              [menuKey]: true,
            }))
          }
          onClose={() =>
            setDeviceRowIdEllipsisOpen((current) => ({
              ...current,
              [menuKey]: false,
            }))
          }
        >
          {hasCreateEditPermission && (
            <Button
              className="w-100 text-center"
              variant="primary"
              onClick={() => {
                openEditFormForSystemOrDevice('device', device);
                setDeviceRowIdEllipsisOpen((current) => ({
                  ...current,
                  [menuKey]: false,
                }));
              }}
            >
              Edit
            </Button>
          )}
          {hasDeletePermission && (
            <Button
              className="w-100 text-center"
              variant="danger"
              onClick={() => {
                prepareSystemOrDeviceForDeletion(device);
                setDeviceRowIdEllipsisOpen((current) => ({
                  ...current,
                  [menuKey]: false,
                }));
              }}
            >
              Remove
            </Button>
          )}
        </BaseEllipsisMenu>
      </div>
    );
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'System Category',
        accessorKey: 'name',
        Cell: ({ row, cell }) => {
          const name = cell.getValue();
          const icon = getCategoryIcon(name);

          return (
            <div className="d-flex gap-3 align-items-center">
              <div>
                <img src={icon} alt={name} width={'30px'} />
              </div>
              <div className="d-flex flex-column text-white fs-6">
                <div>{name}</div>
                <div className="text-break text-secondary">
                  {row.original.systemsCount}{' '}
                  {row.original.systemsCount === 1 ? 'system' : 'systems'}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: 'Systems',
        accessorKey: 'typeCount',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Devices',
        accessorKey: 'devicesCount',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-end gap-3 w-100 pe-1 text-white">
            {row.getIsExpanded() ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
          </div>
        ),
      },
    ];
  }, []);

  const rows = useMemo(() => {
    if (!connectedSystemsHierarchy) return [];

    return connectedSystemsHierarchy.map((category) => ({
      category,
      id: category.id,
      name: category.name,
      typeCount: category.systemsCount,
      systemsCount: category.systemsCount,
      devicesCount: category.devicesCount,
    }));
  }, [connectedSystemsHierarchy]);

  const renderDevices = (deviceGroup, systemRowId) => (
    <div className="px-2 pb-2">
      <div className="border border-secondary border-opacity-50 rounded-2 overflow-hidden bg-primary-shadow">
        {deviceGroup.devices.length > 0 ? (
          <>
            <div className={nestedDropdownHeaderClass}>
              <div className="col-lg-3">Name</div>
              <div className="col-lg-1">Device ID</div>
              <div className="col-lg-2">Type</div>
              <div className="col-lg-2">License</div>
              <div className="col-lg-2">Status</div>
              <div className="col-lg-1">Created</div>
              <div className="col-lg-1" />
            </div>
            {deviceGroup.devices.map((device, deviceIndex) => (
              <div
                key={device.id}
                className={`row mx-0 px-2 py-2 align-items-center border-bottom border-secondary bg-${
                  deviceIndex % 2 ? 'primary-shadow' : 'base'
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => openDeviceView(device)}
              >
                <div className="col-12 col-lg-3">
                  <div className="d-flex align-items-center gap-3">
                    {deviceGroup.deviceType === 'Stage' ? (
                      <div className="bg-light p-1" style={{ marginLeft: '-3px' }}>
                        <img
                          src={getDynamicDeviceIcon(device)}
                          alt={device?.system_type}
                          width={'30px'}
                          height={'49px'}
                        />
                      </div>
                    ) : (
                      <img
                        src={getDynamicDeviceIcon(device)}
                        alt={device?.system_type}
                        width={'30px'}
                      />
                    )}
                    <div>
                      <p className="m-0 fw-semibold">
                        {device?.alias || device?.monitoring_location_name || device?.name}
                      </p>
                      <p className="m-0 text-light opacity-75">
                        {device?.static_id || device?.name}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-1">
                  <span className="d-inline d-lg-none">ID: </span>
                  {hasDisplayValue(getDeviceDisplayId(device)) ? getDeviceDisplayId(device) : ''}
                </div>
                <div className="col-12 col-lg-2">
                  <span className="d-inline d-lg-none">Type: </span>
                  {`${device?.system_type} ${formatDeviceTypeLabel(device?.device_type)}`}
                </div>
                <div className="col-12 col-lg-2">
                  <span className="d-inline d-lg-none">License: </span>
                  {getDeviceLicenseLabel(device, licenseInfo, deviceIndexMap)}
                </div>
                <div className="col-10 col-lg-2 d-flex align-items-center ps-0 pe-0">
                  <DeviceStatusBadge device={device} />
                </div>
                <div className="col-12 col-lg-1 small">
                  <span className="d-inline d-lg-none">Created: </span>
                  {new Date(device?.date_created).toLocaleString()}
                </div>
                {renderDeviceActionMenu(device, `${systemRowId}:${deviceGroup.id}:${device.id}`)}
              </div>
            ))}
          </>
        ) : (
          <div className="px-3 py-3 text-center">No devices in this type.</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {!connectedDevicesIsFetching ? (
        <BaseTable
          columns={columns}
          rows={rows}
          expandable={true}
          enableRowVirtualization={false}
          muiTablePaperProps={{
            sx: {
              height: 'auto',
              maxHeight: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
          muiTableContainerProps={{
            sx: {
              flex: '0 1 auto',
              minHeight: 0,
              maxHeight: '100%',
              overflowY: 'auto',
            },
          }}
          customStyles={{
            cell: {
              cursor: 'pointer',
              padding: '12px 16px',
            },
            header: { padding: '12px 16px' },
            dropdown: {
              backgroundColor: 'var(--color-primary-highlight)',
              color: 'var(--color-primary-highlight-foreground)',
              padding: '0 !important',
            },
          }}
          dropdownMenu={(row) => (
            <div className="w-100 bg-primary">
              <div className={dropdownHeaderClass}>
                <div className="col-lg-2">System Type</div>
                <div className="col-lg-2">System Name</div>
                <div className="col-lg-1">Sys ID</div>
                <div className="col-lg-2">License Info</div>
                <div className="col-lg-2">Device Type</div>
                <div className="col-lg-2">Devices</div>
                <div className="col-lg-1" />
              </div>
              <div className="p-2 m-2">
                {row.category.types.flatMap((type) =>
                  type.systems.map((systemNode) => {
                    const systemRowKey = `${row.category.id}:${type.id}:${systemNode.id}`;
                    // Empty for basic systems, which consume no system license.
                    const systemLicenseLabel = getSystemLicenseLabel(
                      systemNode.sourceSystem,
                      licenseInfo,
                      systemIndexMap,
                    );
                    return (
                      <div
                        key={systemNode.id}
                        className="border-bottom border-secondary overflow-hidden bg-base my-2 rounded-2"
                      >
                        {systemNode.deviceGroups.map((deviceGroup, deviceGroupIndex) => {
                          const groupRowKey = `${systemRowKey}:${deviceGroup.id}`;
                          const isOpen = !!openRows[groupRowKey];
                          const isFirstDeviceGroup = deviceGroupIndex === 0;

                          return (
                            <div
                              key={deviceGroup.id}
                              className={
                                deviceGroupIndex === systemNode.deviceGroups.length - 1
                                  ? ''
                                  : 'border-bottom border-secondary'
                              }
                            >
                              <div
                                className="row mx-1 px-2 py-2 align-items-center bg-base"
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleRow(groupRowKey)}
                              >
                                <div className="col-12 col-lg-2 fw-semibold">
                                  <span className="d-inline d-lg-none text-light opacity-75">
                                    System Type:{' '}
                                  </span>
                                  {type.name}
                                </div>
                                <div className="col-12 col-lg-2">
                                  <div className="d-flex align-items-center gap-3">
                                    <img
                                      src={getSystemIcon(systemNode.sourceSystem.system_type)}
                                      alt={systemNode.sourceSystem.system_type}
                                      width={'30px'}
                                      className="flex-shrink-0"
                                    />
                                    <div className="min-w-0">
                                      <div className="fw-semibold">{systemNode.name}</div>
                                      <div className="text-light opacity-75 text-truncate">
                                        {systemNode.sourceSystem.email || 'webops@rescalert.com'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-lg-1">
                                  <span className="d-inline d-lg-none text-light opacity-75">
                                    ID:{' '}
                                  </span>
                                  {hasDisplayValue(getSystemDisplayId(systemNode.sourceSystem))
                                    ? getSystemDisplayId(systemNode.sourceSystem)
                                    : ''}
                                </div>
                                <div className="col-12 col-lg-2 text-secondary">
                                  {systemLicenseLabel && (
                                    <>
                                      <span className="d-inline d-lg-none text-light opacity-75">
                                        License Info:{' '}
                                      </span>
                                      {systemLicenseLabel}
                                    </>
                                  )}
                                </div>
                                <div className="col-12 col-lg-2">
                                  <div className="d-flex align-items-center gap-3">
                                    <img
                                      src={
                                        deviceImageList[deviceGroup.deviceType] ||
                                        deviceImageList['RESCALERT']
                                      }
                                      alt={deviceGroup.deviceType}
                                      width={'30px'}
                                    />
                                    <span>{formatDeviceTypeLabel(deviceGroup.deviceType)}</span>
                                  </div>
                                </div>
                                <div className="col-12 col-lg-2">
                                  <span className="d-inline d-lg-none text-light opacity-75">
                                    Devices:{' '}
                                  </span>
                                  {deviceGroup.devices.length}{' '}
                                  {deviceGroup.devices.length === 1 ? 'device' : 'devices'}
                                </div>
                                <div
                                  className="col-12 col-lg-1 d-flex justify-content-lg-end align-items-center gap-3"
                                  onClick={(event) => event.stopPropagation()}
                                  onMouseDown={(event) => event.stopPropagation()}
                                >
                                  {isFirstDeviceGroup &&
                                    renderSystemActionMenu(systemNode, systemRowKey)}
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="p-0 text-white border-0"
                                    onClick={() => toggleRow(groupRowKey)}
                                    aria-expanded={isOpen}
                                    aria-controls={`${groupRowKey}-devices`}
                                    aria-label={
                                      isOpen
                                        ? `Collapse ${formatDeviceTypeLabel(deviceGroup.deviceType)} devices`
                                        : `Expand ${formatDeviceTypeLabel(deviceGroup.deviceType)} devices`
                                    }
                                  >
                                    {isOpen ? (
                                      <MdExpandLess size={20} />
                                    ) : (
                                      <MdExpandMore size={20} />
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <Collapse in={isOpen}>
                                <div id={`${groupRowKey}-devices`}>
                                  {renderDevices(deviceGroup, systemRowKey)}
                                </div>
                              </Collapse>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
          )}
        />
      ) : (
        <WidgetLoadingAnimation />
      )}
    </>
  );
};

export default Table;
